<template>
  <div class="medical-feedback-form">
    <!-- 非微信环境提示 -->
    <div v-if="!isWechat" class="wechat-warning">
      <div class="warning-box">
        <h2>⚠️ 请在微信内打开</h2>
        <p>本留言板仅支持在微信客户端使用，请使用微信扫描二维码访问</p>
      </div>
    </div>

    <!-- 提交成功提示 + 返回按钮 -->
    <div v-else-if="showSuccess" class="success-warning">
      <div class="success-box">
        <h2>✅ 提交成功！</h2>
        <p>感谢您的反馈，我们会尽快跟进处理</p>
        <button class="back-btn" @click="backToForm">返回填写界面</button>
      </div>
    </div>

    <!-- 微信环境正常显示表单 -->
    <div v-else class="form-card">
      <div class="form-header">
        <h1>📋 便民留言板</h1>
        <p>请填写以下信息，我们会尽快跟进处理</p>
      </div>
      <div class="form-body">
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label>
              📅 填写日期 <span class="required-star">*</span>
            </label>
            <input
                type="date"
                v-model="form.date"
                :max="maxDate"
                required
            >
            <div v-if="errors.dateError" class="message-error field-error">
              {{ errors.dateError }}
            </div>
          </div>

          <div class="form-group">
            <label>
              🏥 关联科室 <span class="required-star">*</span>
              <span class="optional-tag">可多选</span>
            </label>
            <div class="checkbox-group">
              <label class="checkbox-item" v-for="dept in departmentsOptions" :key="dept.value">
                <input type="checkbox" :value="dept.value" v-model="form.departments">
                <span>{{ dept.icon }} {{ dept.label }}</span>
              </label>
            </div>

            <div
                class="other-department-input"
                v-if="form.departments.includes('其他')"
                style="margin-top: 12px;"
            >
              <input
                  v-model="otherDepartment"
                  placeholder="请输入其他科室名称"
                  style="border: none; border-bottom: 1px solid #2d3748; outline: none; width: 100%; font-size: 15px; padding: 4px 0;"
              >
            </div>

            <div v-if="errors.deptError" class="message-error field-error">
              {{ errors.deptError }}
            </div>
          </div>

          <div class="form-group">
            <label>💬 意见和建议 <span class="required-star">*</span></label>
            <textarea
                v-model="form.suggestion"
                placeholder="如您在本院就医期间遇到相关问题，或是对我院医疗服务、医院管理有宝贵意见与改进建议，欢迎您留言反馈"
                rows="4"
            ></textarea>
            <div v-if="errors.suggestionError" class="message-error field-error">
              {{ errors.suggestionError }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>👤 姓名 <span class="optional-tag">选填</span></label>
              <input
                  type="text"
                  v-model="form.name"
                  placeholder="为方便反馈，建议留下姓名"
              >
            </div>

            <div class="form-group">
              <label>📞 联系方式 <span class="optional-tag">选填</span></label>
              <input
                  type="tel"
                  v-model="form.contact"
                  placeholder="为方便反馈，建议留下联系方式"
              >
              <div v-if="errors.contactError" class="message-error field-error">
                {{ errors.contactError }}
              </div>
            </div>
          </div>

          <div class="button-group">
            <button type="button" class="btn btn-secondary" @click="resetForm" :disabled="isSubmitting">
              🧹 重新填写
            </button>
            <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting || !jwtReady"
            >
              {{ isSubmitting ? '提交中...' : (jwtReady ? '✨ 提交反馈' : '⏳ 身份验证中...') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import request from '@/utils/request';
import { Base64 } from 'js-base64';

export default {
  name: 'MedicalFeedbackForm',
  props: {
    maxDate: {
      type: String,
      default: '2099-12-31'
    },
    departments: {
      type: Array,
      default: () => [
        { value: '体检', label: '体检', icon: '🏋️' },
        { value: '住院', label: '住院', icon: '🛏️' },
        { value: '门诊', label: '门诊', icon: '🏥' },
        { value: '疫苗', label: '疫苗', icon: '💉' },
        { value: '大亚湾院区', label: '大亚湾院区', icon: '🏛️' },
        { value: '仲恺院区', label: '仲恺院区', icon: '🏢' },
        { value: '其他', label: '其他', icon: '✏️' }
      ]
    }
  },
  setup(props) {
    // 微信环境
    const isWechat = ref(true)
    // 提交成功显示
    const showSuccess = ref(false)
    // 提交中状态（移除倒计时相关变量）
    const isSubmitting = ref(false)
    // JWT 是否已准备就绪（用于控制提交按钮）
    const jwtReady = ref(false)

    // 表单数据
    const form = reactive({
      date: '',
      departments: [],
      otherDepartment: '',
      suggestion: '',
      name: '',
      contact: '',
      jwt: ''
    })

    // 错误
    const errors = reactive({
      dateError: '',
      deptError: '',
      contactError: '',
      suggestionError: ''
    })

    const departmentsOptions = ref(props.departments)
    const otherDepartment = ref('')

    // ============== 1. 判断微信环境 ==============
    const checkWechatEnv = () => {
      const ua = navigator.userAgent.toLowerCase()
      const isWechatBrowser = ua.includes('micromessenger') && !ua.includes('wxwork')
      isWechat.value = isWechatBrowser
    }

    // ============== 2. 获取微信OpenID（从URL参数） ==============
    const getUrlParam = (name) => {
      const reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)")
      const r = window.location.search.substr(1).match(reg)
      if (r != null) return unescape(r[2])
      return null
    }

    const getWxOpenId = async () => {
      try {
        const code =getUrlParam('code')
        if (code) {
          console.log('拿到授权code：', code)
          const response = await request.get('/api/WencatControl/getOpenID', {
            params: {
              code: code,
              dz: 1
            }
          })

          console.log('openid响应：', response)

          if (response.state == "200") {
            form.jwt = response.content
            jwtReady.value = true
            console.log(response.message)
          } else {
            const wxAuthUrl = `https://mp.weixin.qq.com/mp/profile_ext?action=home&__biz=MzI0NDM1NzAyNw==#wechat_redirect`
            window.location.href = wxAuthUrl
            console.log(response.message)
          }
        } else {
          console.log('无code，需要跳微信授权页')
          const redirectUri = encodeURIComponent('https://qy.hzzfy.com')
          const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=wxaa9133947fc3b8fb&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`
          console.log("authUrl1",authUrl)
          window.location.href = authUrl
        }
      } catch (err) {
        console.error('判断是否关注服务号异常：', err)
        jwtReady.value = false
      }
    }

    // ============== 3. 返回表单页面 ==============
    const backToForm = () => {
      showSuccess.value = false
      resetForm()
    }

    // 清空错误
    const clearFieldErrors = () => {
      errors.dateError = ''
      errors.deptError = ''
      errors.contactError = ''
      errors.suggestionError = ''
    }

    // 联系方式验证
    const validateContact = (contact) => {
      if (!contact || contact.trim() === '') return true
      const phone = contact.trim()
      const mobileReg = /^1[3-9]\d{9}$/
      const telReg = /^(0\d{2,3}-?)?\d{7,8}$/
      return mobileReg.test(phone) || telReg.test(phone)
    }

    // 获取今天日期
    const getTodayString = () => {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    // 初始化日期
    const initDate = () => {
      if (!form.date) {
        form.date = getTodayString()
      }
    }

    // 重置表单
    const resetForm = () => {
      form.departments = []
      otherDepartment.value = ''
      form.suggestion = ''
      form.name = ''
      form.contact = ''
      form.date = getTodayString()
      isSubmitting.value = false   // 重置提交状态
      clearFieldErrors()
    }

    // 表单验证（新增 jwt 存在性校验）
    const validateForm = () => {
      let isValid = true
      clearFieldErrors()

      if (!form.jwt) {
        errors.dateError = '身份验证中，请稍后重试'
        isValid = false
      }

      if (!form.date || form.date.trim() === '') {
        errors.dateError = '请选择填写日期'
        isValid = false
      }

      if (!form.departments || form.departments.length === 0) {
        errors.deptError = '请至少选择一个科室'
        isValid = false
      }

      if (form.departments.includes('其他') && !otherDepartment.value.trim()) {
        errors.deptError = '请输入其他科室名称'
        isValid = false
      }

      if (!form.suggestion || form.suggestion.trim() === '') {
        errors.suggestionError = '请填写意见和建议'
        isValid = false
      }

      if (!validateContact(form.contact)) {
        errors.contactError = '请输入有效的手机号码或座机号码'
        isValid = false
      }

      return isValid
    }

    // 将科室数组转换为逗号拼接的字符串
    const convertDepartmentsToString = (departmentsArray, otherDeptValue) => {
      if (!departmentsArray || departmentsArray.length === 0) return ''
      let processedDepts = [...departmentsArray]
      if (processedDepts.includes('其他') && otherDeptValue && otherDeptValue.trim()) {
        const index = processedDepts.indexOf('其他')
        processedDepts.splice(index, 1, otherDeptValue.trim())
      }
      return processedDepts.join(',')
    }

    // 提交（修改点：移除倒计时，添加提交中标识，响应后重置状态）
    const handleSubmit = async () => {
      if (isSubmitting.value) return
      const isValid = validateForm()
      if (!isValid) return

      // 开始提交，按钮显示“提交中...”
      isSubmitting.value = true

      // 处理科室字段
      const departmentsString = convertDepartmentsToString(form.departments, otherDepartment.value)

      const submitData = {
        ...form,
        departments: departmentsString,
        otherDepartment: otherDepartment.value.trim(),
      }

      const encryptedData = Base64.encode(JSON.stringify(submitData))

      try {
        const submitresponse = await request.post('/api/FeedControl/insetFeed', {
          result: encryptedData,
          url: window.location.origin + '/'
        })

        console.log("url", window.location.origin)
        console.log('提交响应：', submitresponse)

        if (submitresponse.state == "200") {
          showSuccess.value = true
          console.log("提交成功", submitresponse.message)
        } else if (submitresponse.state == "203") {
          alert("页面停留过久刷新界面重新填写")
          const redirectUri = encodeURIComponent(window.location.origin)
          const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=wx0f299ff5c8b94df8&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`
          window.location.href = authUrl
          console.log("提交失败", submitresponse.message)
        } else {
          alert("提交失败：" + (submitresponse.message || "未知错误，请重试"))
          console.log("提交失败", submitresponse.message)
        }
      } catch (error) {
        console.error("提交请求异常：", error)
        alert("网络异常，提交失败，请检查网络后重试")
      } finally {
        // 只有在没有跳转或成功显示成功界面的情况下重置提交状态
        // 注意：如果 state == 200，成功界面会隐藏表单，重置状态无影响；如果跳转页面，后续代码不会执行
        isSubmitting.value = false
      }
    }

    onMounted(() => {



      checkWechatEnv()
      getWxOpenId()
      initDate()
    })

    return {
      isWechat,
      showSuccess,
      form,
      errors,
      otherDepartment,
      departmentsOptions,
      isSubmitting,
      jwtReady,
      handleSubmit,
      resetForm,
      backToForm,
      getTodayString
    }
  }
}
</script>

<style scoped>
/* 全局适配：手机优先，兼容微信小程序 web-view */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 统一字体，提升小程序内渲染一致性 */
.medical-feedback-form {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: transparent;
}

/* 非微信环境提示样式 */
.wechat-warning {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  padding: 20px;
}
.warning-box {
  background: #fff;
  padding: 40px 20px;
  border-radius: 20px;
  text-align: center;
  max-width: 90%;
  box-shadow: 0 8px 30px rgba(0,0,0,0.08);
}
.warning-box h2 {
  color: #f56c6c;
  margin-bottom: 16px;
  font-size: 20px;
}
.warning-box p {
  color: #666;
  font-size: 15px;
  line-height: 1.6;
}

/* 提交成功样式 */
.success-warning {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  padding: 20px;
}
.success-box {
  background: #fff;
  padding: 40px 20px;
  border-radius: 20px;
  text-align: center;
  max-width: 90%;
  box-shadow: 0 8px 30px rgba(0,0,0,0.08);
}
.success-box h2 {
  color: #10b981;
  margin-bottom: 16px;
  font-size: 20px;
}
.success-box p {
  color: #666;
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 24px;
}
.back-btn {
  background: #1e4a6e;
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.back-btn:active {
  opacity: 0.8;
}

/* 主容器 */
.medical-feedback-form {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #f5f7fa;
}

/* 表单卡片 */
.form-card {
  width: 100%;
  max-width: 780px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #1e4a6e 0%, #2c6b97 100%);
  padding: 24px 20px;
  color: white;
  text-align: center;
}

.form-header h1 {
  font-size: 22px;
  margin-bottom: 6px;
}

.form-header p {
  font-size: 14px;
  opacity: 0.9;
}

.form-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.form-row .form-group {
  flex: 1;
  min-width: 260px;
}

label {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 8px;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 6px;
}

.required-star {
  color: #e03a3a;
}

.optional-tag {
  background: #f0f3f7;
  color: #64748b;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
}

/* 输入框与文本域基础样式，移除iOS默认圆角 */
input, textarea {
  width: 100%;
  font-size: 16px;
  padding: 14px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: all 0.2s;
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
}

input:focus, textarea:focus {
  border-color: #2c6b97;
  box-shadow: 0 0 0 3px rgba(44, 107, 151, 0.15);
}

textarea {
  resize: vertical;
  min-height: 120px;
}

/* 复选框组 */
.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  background: #f9fbfd;
  padding: 16px;
  border-radius: 12px;
  border: 1.5px solid #f0f4f8;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 2px;
}

/* 自定义复选框，解决 accent-color 在部分安卓/鸿蒙微信 webview 的兼容问题 */
.checkbox-item input[type="checkbox"] {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  margin: 0;
  padding: 0;
}
.checkbox-item input[type="checkbox"]:checked {
  background: #2c6b97;
  border-color: #2c6b97;
}
.checkbox-item input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 6px;
  height: 11px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* 按钮组 —— 修改点：确保始终并排显示，移除垂直排列的媒体查询 */
.button-group {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap; /* 允许换行但保持水平，不会变成列 */
}

.btn {
  flex: 1;
  min-width: 140px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: opacity 0.2s, background-color 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.btn:active {
  opacity: 0.85;
}

.btn-primary {
  background: #1e4a6e;
  color: white;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
}
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 错误信息 */
.field-error {
  margin-top: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #b91c1c;
  background: #fff5f5;
  border-radius: 6px;
}

/* 修复手机日期输入框超出边框问题 */
input[type="date"] {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 响应式 —— 修改点：移除 button-group 的垂直排列，始终并排 */
@media (max-width: 480px) {
  .medical-feedback-form {
    padding: 8px;
  }
  .form-header h1 {
    font-size: 20px;
  }
  .form-body {
    padding: 16px;
  }
  /* 原 .button-group 的 flex-direction: column 已删除，保持水平并排 */
}
</style>